import type { RegionOption } from '~/composables/useRegionSwitcher';

import { useAnalyticsService } from '~/composables/core';
import { useGeoPreferenceTargetInput } from '~/composables/useGeoPreferenceTargetInput';
import { useRegionSwitcher } from '~/composables/useRegionSwitcher';
import { EVENTS } from '~/constants/events';
import { isGeoMirroredPath } from '~/constants/routes/unified';
import { GEO_REDIRECT_TRACKING_COOKIE, parseRegionPreference } from '~/utils/geo';
import { isGeoPromptEligible, resolveGeoPreferenceTarget } from '~/utils/geoRedirect';

/** Where a region switch was initiated — the analytics `source` discriminator. */
export type RegionSwitchSource = 'selector' | 'footer' | 'auto' | 'redirect';

export interface PendingRegionSwitch {
  region: RegionOption;
  source: RegionSwitchSource;
}

interface SelectorState {
  open: boolean;
  /** Which surface opened the selector — carried through to the switch events. */
  source: RegionSwitchSource;
  /** The first-visit selector is a deliberate choice and can't be closed away. */
  locked: boolean;
}

const SELECTOR_STATE = 'geo-selector';
const PENDING_SWITCH_STATE = 'geo-pending-region-switch';

/**
 * Preference-based geo routing — the single code path behind every region
 * switch surface (wrong-region selector, footer switcher, and the auto-routing
 * prompt), so the spec's rules hold everywhere:
 *
 *  - a switch first checks the current page has an equivalent in the
 *    destination region (`isGeoMirroredPath` — the same source that declares
 *    hreflang alternates); with one, the path carries over;
 *  - without one, a confirmation dialog offers the destination homepage;
 *    declining keeps the page, leaves the stored preference untouched, and
 *    quietens prompts for the session;
 *  - the stored preference always overrides the detected geo, and crawlers
 *    are never prompted.
 *
 * Mirrored-path auto-redirects for returning visitors live in
 * `middleware/09.geo-preference-routing.global.ts`; this composable covers the
 * interactive surfaces plus the unmirrored auto-routing prompt. Both build on
 * the same `resolveGeoPreferenceTarget` policy and `useGeoPreferenceTargetInput`
 * assembly, so the SSR redirect and the client prompts can never disagree.
 */
export const useGeoRouting = () => {
  const route = useRoute();
  const analytics = useAnalyticsService();
  const { regions, currentRegion, sourcePath, detectedGeo } = useRegionSwitcher();
  const { input, preference, dismissed } = useGeoPreferenceTargetInput();

  const selector = useState<SelectorState>(SELECTOR_STATE, () => ({
    open: false,
    source: 'selector',
    locked: false,
  }));
  const pendingSwitch = useState<PendingRegionSwitch | null>(PENDING_SWITCH_STATE, () => null);

  /** Every region event carries the shared context; payload keys stay byte-identical. */
  const track = (event: string, payload: Record<string, unknown>): void => {
    analytics.track(event, {
      // The IP-detected region (coarse USA/AUS/OTHER, not PII). Lets us measure
      // how often detection disagrees with the visitor's actual choice — the
      // signal for whether we're defaulting people to the wrong region.
      detectedRegion: detectedGeo.value,
      page: route.name,
      ...payload,
    });
  };

  const trackSwitch = (switched: { fromRegion?: string; toRegion?: string; targetUrl?: string; source: RegionSwitchSource }): void => {
    track(EVENTS.UNIFIED_PUBLIC_REGION_SWITCHED, {
      fromRegion: switched.fromRegion ?? currentRegion.value?.geo,
      toRegion: switched.toRegion,
      path: sourcePath.value,
      targetUrl: switched.targetUrl,
      source: switched.source,
    });
  };

  /**
   * First visit (no stored preference) on the *wrong* region: the visitor's
   * detected region is one we serve and it isn't the one this deployment
   * serves. A visitor already on their own region's site is never interrupted —
   * changing region is a footer affair. `OTHER` is not a mismatch: we have no
   * better site to offer, so those visitors are left alone too.
   * Stand-downs (schools/auth/bot/dismissed/unlisted-cluster/auth-params) are
   * the policy's `isGeoPromptEligible`.
   */
  const shouldAutoShowSelector = computed<boolean>(() => {
    const bag = input.value;
    const detected = parseRegionPreference(detectedGeo.value);
    return isGeoPromptEligible({ ...bag, path: route.path, query: route.query })
      && !bag.preference
      && !!detected
      && !!bag.currentGeo
      && detected !== bag.currentGeo;
  });

  /**
   * Returning visitor whose preference mismatches the served region on a page
   * with no equivalent — the middleware can't redirect, so the UI offers the
   * destination homepage instead. Pure delegation to the shared policy;
   * mirrored paths are the middleware's redirect, not a prompt.
   */
  const autoRouteTarget = computed<RegionOption | null>(() => {
    const target = resolveGeoPreferenceTarget({ ...input.value, path: route.path, query: route.query });
    if (!target || target.mirrored) {
      return null;
    }
    return regions.value.find(region => region.geo === target.cluster.geo) ?? null;
  });

  const openSelector = (source: RegionSwitchSource, options: { locked?: boolean } = {}): void => {
    selector.value = { open: true, source, locked: options.locked ?? false };
    track(EVENTS.UNIFIED_PUBLIC_REGION_SELECTOR_SHOWN, {
      region: currentRegion.value?.geo,
      source,
    });
  };

  /**
   * Closing the dismissable selector without choosing (Escape, click-outside,
   * close button) — the abandonment counterpart to SELECTOR_SHOWN, without
   * which that surface's drop-off can't be derived. Guarded on the selector
   * still being open so a choice, which closes it first, never reports as one.
   */
  const dismissSelector = (): void => {
    if (!selector.value.open) {
      return;
    }
    selector.value.open = false;
    track(EVENTS.UNIFIED_PUBLIC_REGION_SELECTOR_DISMISSED, {
      region: currentRegion.value?.geo,
      source: selector.value.source,
    });
  };

  /**
   * The one switch entry point. Choosing the served region just stores the
   * preference; choosing the other region carries the path over when it exists
   * everywhere, and otherwise stages the confirmation dialog. The source
   * defaults to whichever surface opened the selector.
   */
  const selectRegion = (region: RegionOption, source: RegionSwitchSource = selector.value.source): void => {
    selector.value.open = false;

    if (region.isCurrent) {
      preference.value = region.geo;
      track(EVENTS.UNIFIED_PUBLIC_REGION_PREFERENCE_SET, { region: region.geo, source });
      return;
    }

    if (isGeoMirroredPath(route.path)) {
      preference.value = region.geo;
      trackSwitch({ toRegion: region.geo, targetUrl: region.switchUrl, source });
      void navigateTo(region.switchUrl, { external: true });
      return;
    }

    // Picking in the selector *is* the region choice, so it is stored before the
    // dialog rather than by it: the confirmation only decides whether to leave
    // an unmirrored page, and declining must not drop the choice and re-show the
    // locked first-visit selector on the next load. A footer switch is a
    // navigation the visitor can still cancel, so it stores on confirm only.
    if (source === 'selector') {
      preference.value = region.geo;
    }

    pendingSwitch.value = { region, source };
  };

  const confirmPendingSwitch = (): void => {
    const pending = pendingSwitch.value;
    if (!pending) {
      return;
    }
    preference.value = pending.region.geo;
    pendingSwitch.value = null;
    // switchUrl on an unmirrored path is the destination home with the sanitised
    // query carried over — the pending switch only exists for unmirrored paths,
    // so this preserves campaign/UTM params the bare home url would drop.
    trackSwitch({ toRegion: pending.region.geo, targetUrl: pending.region.switchUrl, source: pending.source });
    void navigateTo(pending.region.switchUrl, { external: true });
  };

  /** Stay on the current page: the stored preference is deliberately untouched. */
  const declinePendingSwitch = (): void => {
    const pending = pendingSwitch.value;
    if (!pending) {
      return;
    }
    track(EVENTS.UNIFIED_PUBLIC_REGION_DISMISSED, {
      region: currentRegion.value?.geo,
      source: pending.source,
    });
    pendingSwitch.value = null;
    // Only an auto-routing decline ("stay in this region for now") quietens the
    // session's prompts and redirects. Declining an explicit switch the visitor
    // themselves started is a one-off cancel — it must not disable preference
    // routing toward a preference they never abandoned.
    if (pending.source === 'auto') {
      dismissed.value = true;
    }
  };

  /**
   * Fire the switch event for a completed SSR preference-redirect. The 302
   * happens server-side where no analytics provider runs
   * (`plugins/06.analytics.ts` registers client-only), so the middleware drops
   * a marker cookie and the landing page reports it here — otherwise the
   * feature's primary path is invisible to Mixpanel.
   */
  const trackCompletedRedirect = (): void => {
    const marker = useCookie<string | null>(GEO_REDIRECT_TRACKING_COOKIE, { sameSite: 'lax', secure: true });
    if (!marker.value) {
      return;
    }
    const fromRegion = parseRegionPreference(marker.value) ?? undefined;
    marker.value = null;
    trackSwitch({
      fromRegion,
      toRegion: currentRegion.value?.geo,
      // Absolute, like every other switch target — only mirrored paths redirect,
      // so the served region's switchUrl is the page we landed on. The relative
      // path is already reported once, as `path`.
      targetUrl: currentRegion.value?.switchUrl,
      source: 'redirect',
    });
  };

  /**
   * The mount sequence for the prompt surfaces, in priority order: report a
   * just-completed SSR redirect, then the first-visit selector (locked — it is
   * a deliberate choice), else the returning-visitor auto-route prompt. Runs
   * after mount, never in SSR markup: crawlers get clean HTML and hydration
   * stays flash-free.
   */
  const startPrompts = (): void => {
    trackCompletedRedirect();
    if (shouldAutoShowSelector.value) {
      openSelector('selector', { locked: true });
      return;
    }
    if (autoRouteTarget.value && !pendingSwitch.value) {
      pendingSwitch.value = { region: autoRouteTarget.value, source: 'auto' };
      // The prompt's shown-denominator: without it, impressions and abandonment
      // of this surface can't be derived from SWITCHED/DISMISSED alone.
      track(EVENTS.UNIFIED_PUBLIC_REGION_PROMPT_SHOWN, {
        region: currentRegion.value?.geo,
        offeredRegion: autoRouteTarget.value.geo,
        source: 'auto',
      });
    }
  };

  return {
    regions,
    selector,
    pendingSwitch,
    openSelector,
    dismissSelector,
    selectRegion,
    confirmPendingSwitch,
    declinePendingSwitch,
    startPrompts,
  };
};
